import React, { useState } from 'react'
import { Box, Typography, TextField, Select, MenuItem, FormControl, InputLabel, Button, CircularProgress, IconButton } from '@mui/material'
import { styled } from '@mui/material/styles'
import { motion } from 'framer-motion'
import { AddCircle, Delete } from '@mui/icons-material'
import { calculatePortfolioYield, calculateEndOfYearValue } from './Calculations'
import { EducationTooltip, getEducationMessages } from './Education'

const AnimatedCard = styled(motion.div)`
  background: #0a0a0a;
  padding: 32px;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(120, 60, 180, 0.3);
  border: 1px solid rgba(165, 121, 255, 0.2);
  position: relative;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #8a4fff, #d946ef, #8a4fff);
  }
`

const Title = styled(Typography)`
  font-family: 'Cinzel', serif;
  font-weight: 600;
  letter-spacing: 1px;
  background: linear-gradient(90deg, #ffffff, #d1b3ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  margin-bottom: 24px;
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 2px;
    background: linear-gradient(90deg, #8a4fff, transparent);
    margin: 12px 0 24px;
  }
`

const AssetCard = styled(Box)`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: rgba(138, 79, 255, 0.05);
  border-radius: 12px;
  margin-bottom: 16px;
  border: 1px solid rgba(138, 79, 255, 0.2);
`

const GradientButton = styled(Button)`
  background: linear-gradient(45deg, #8a4fff, #d946ef);
  color: white;
  padding: 12px 24px;
  border-radius: 12px;
  font-weight: 600;
  text-transform: none;
  &:hover {
    background: linear-gradient(45deg, #9b5bff, #e557ff);
  }
`

const AddAssetButton = styled(Button)`
  background: rgba(138, 79, 255, 0.1);
  color: #8a4fff;
  padding: 8px 16px;
  border-radius: 12px;
  text-transform: none;
  border: 1px solid rgba(138, 79, 255, 0.3);
  &:hover {
    background: rgba(138, 79, 255, 0.2);
  }
`

const PortfolioBuilder = ({ portfolio, setPortfolio, setResult, availableAssets, loadingAssets }) => {
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [totalPortfolioValue, setTotalPortfolioValue] = useState('')
  const strategies = ['aggressive', 'balanced', 'safe']
  const educationMessages = getEducationMessages()

  const addAsset = () => {
    setPortfolio([...portfolio, { symbol: availableAssets[0]?.symbol || '', percentage: '', strategy: strategies[0], shares: 0 }])
  }

  const updateAsset = (index, field, value) => {
    const updatedPortfolio = [...portfolio]
    updatedPortfolio[index][field] = field === 'percentage' ? (value === '' ? '' : parseFloat(value) || '') : value

    if (field === 'percentage' || field === 'symbol') {
      const asset = availableAssets.find(a => a.symbol === updatedPortfolio[index].symbol)
      const parsedTotalValue = parseFloat(totalPortfolioValue) || 0
      if (asset && parsedTotalValue > 0 && updatedPortfolio[index].percentage !== '') {
        const allocationValue = (updatedPortfolio[index].percentage / 100) * parsedTotalValue
        updatedPortfolio[index].shares = Math.floor(allocationValue / asset.price)
      } else {
        updatedPortfolio[index].shares = 0
      }
    }

    setPortfolio(updatedPortfolio)
  }

  const updateTotalPortfolioValue = (value) => {
    setTotalPortfolioValue(value)

    const parsedTotalValue = parseFloat(value) || 0
    const updatedPortfolio = [...portfolio]
    updatedPortfolio.forEach((asset, index) => {
      const stock = availableAssets.find(a => a.symbol === asset.symbol)
      if (stock && parsedTotalValue > 0 && asset.percentage !== '') {
        const allocationValue = (asset.percentage / 100) * parsedTotalValue
        updatedPortfolio[index].shares = Math.floor(allocationValue / stock.price)
      } else {
        updatedPortfolio[index].shares = 0
      }
    })
    setPortfolio(updatedPortfolio)
  }

  const removeAsset = (index) => {
    setPortfolio(portfolio.filter((_, i) => i !== index))
  }

  const validatePortfolio = () => {
    if (portfolio.length === 0) return 'Portfolio cannot be empty'
    const parsedTotalValue = parseFloat(totalPortfolioValue) || 0
    if (parsedTotalValue <= 0) return 'Total portfolio value must be greater than 0'
    const totalPercentage = portfolio.reduce((sum, asset) => sum + (parseFloat(asset.percentage) || 0), 0)
    if (totalPercentage > 100) return 'Total allocation percentage cannot exceed 100%'
    for (const asset of portfolio) {
      if (!asset.symbol) return 'Please select a stock for each entry'
      if (asset.percentage === '' || asset.percentage <= 0) return 'Percentage must be a positive number'
    }
    return null
  }

  const calculatePortfolio = () => {
    const validationError = validatePortfolio()
    if (validationError) {
      setError(validationError)
      return
    }

    setLoading(true)
    setError(null)

    const formattedPortfolio = portfolio.map(asset => ({
      symbol: asset.symbol,
      amount: asset.shares,
      strategy: asset.strategy
    }))

    const portfolioYield = calculatePortfolioYield(formattedPortfolio, availableAssets)
    const endOfYear = calculateEndOfYearValue(formattedPortfolio, availableAssets)

    setResult({
      portfolioYield,
      totalValue: endOfYear.totalInvestment,
      annualDividend: endOfYear.annualDividend,
      endOfYearValue: endOfYear.endOfYearValue,
      calculatedAt: new Date().toISOString(),
      sharesBreakdown: portfolio.map(asset => ({
        symbol: asset.symbol,
        shares: asset.shares,
        percentage: asset.percentage
      }))
    })
    setLoading(false)
  }

  return (
    <AnimatedCard>
      <Title variant="h4">Build Your Portfolio</Title>
      <TextField
        label="Total Portfolio Value ($)"
        type="number"
        value={totalPortfolioValue}
        onChange={(e) => updateTotalPortfolioValue(e.target.value)}
        sx={{
          mb: 2,
          width: '100%',
          input: { color: '#ffffff' },
          label: { color: '#b0b0b0' },
          '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
            '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
          }
        }}
      />
      {portfolio.length === 0 && (
        <Typography color="#a0a0a0" fontStyle="italic" sx={{ mb: 2 }}>
          Add stocks to create your dividend portfolio
        </Typography>
      )}
      {portfolio.map((asset, index) => (
        <AssetCard key={index}>
          <FormControl sx={{ flex: 1 }}>
            <InputLabel sx={{ color: '#b0b0b0' }}>Stock</InputLabel>
            <Select
              value={asset.symbol}
              onChange={(e) => updateAsset(index, 'symbol', e.target.value)}
              label="Stock"
              sx={{
                color: '#ffffff',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.23)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
              }}
            >
              {availableAssets.map(stock => (
                <MenuItem
                  key={stock.symbol}
                  value={stock.symbol}
                  sx={{
                    '&:hover': { backgroundColor: 'rgba(138, 79, 255, 0.1)' },
                    '&.Mui-selected': { backgroundColor: 'rgba(138, 79, 255, 0.2)' }
                  }}
                >
                  {stock.symbol}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Allocation (%)"
            type="number"
            value={asset.percentage}
            onChange={(e) => updateAsset(index, 'percentage', e.target.value)}
            sx={{
              flex: 1,
              input: { color: '#ffffff' },
              label: { color: '#b0b0b0' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
              }
            }}
          />
          <FormControl sx={{ flex: 1 }}>
            <InputLabel sx={{ color: '#b0b0b0' }}>Strategy</InputLabel>
            <Select
              value={asset.strategy}
              onChange={(e) => updateAsset(index, 'strategy', e.target.value)}
              label="Strategy"
              sx={{
                color: '#ffffff',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.23)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
              }}
            >
              {strategies.map(s => (
                <MenuItem
                  key={s}
                  value={s}
                  sx={{
                    '&:hover': { backgroundColor: 'rgba(138, 79, 255, 0.1)' },
                    '&.Mui-selected': { backgroundColor: 'rgba(138, 79, 255, 0.2)' }
                  }}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography sx={{ color: '#ffffff', flex: 1 }}>
            Shares: {asset.shares}
          </Typography>
          <IconButton onClick={() => removeAsset(index)} sx={{ color: '#d946ef' }}>
            <Delete />
          </IconButton>
        </AssetCard>
      ))}
      <AddAssetButton
        startIcon={<AddCircle />}
        onClick={addAsset}
        disabled={loadingAssets}
      >
        Add Stock
      </AddAssetButton>
      <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(138, 79, 255, 0.2)' }}>
        <GradientButton
          onClick={calculatePortfolio}
          disabled={loading || loadingAssets}
        >
          {loading ? <CircularProgress size={24} sx={{ color: '#ffffff' }} /> : 'Calculate Portfolio'}
        </GradientButton>
      </Box>
      {error && (
        <Box sx={{ 
          mt: 3, 
          p: 2, 
          bgcolor: 'rgba(217, 70, 239, 0.1)', 
          borderRadius: 2, 
          borderLeft: '4px solid #d946ef',
          display: 'flex',
          alignItems: 'center'
        }}>
          <Typography color="#ff6b6b" sx={{ flexGrow: 1 }}>{error}</Typography>
          <IconButton onClick={() => setError(null)} sx={{ color: '#d946ef' }}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      )}
      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="#b0b0b0">
          Learn more about <EducationTooltip title={educationMessages.portfolioYield}>portfolio yield</EducationTooltip> and how it affects your dividends.
        </Typography>
      </Box>
    </AnimatedCard>
  )
}

export default PortfolioBuilder